import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard, Users, BookOpen, UserCog, Settings, LogOut,
  GraduationCap, ChevronDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ProSidebar = ({ items }) => {
  const location = useLocation();
  
  return (
    <aside className="w-64 bg-white border-r border-gray-200 min-h-screen p-4">
      <div className="flex items-center gap-3 px-2 py-4 mb-6">
        <img src="/logo.png" alt="Growing Minds" className="h-12 w-auto" />
      </div>
      
      <nav className="space-y-1">
        {items.map((item, index) => {
          // Handle dividers
          if (item.divider) {
            return (
              <div key={`divider-${index}`} className="pt-4 pb-2">
                <div className="border-t border-gray-200 mb-2"></div>
                <span className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  {item.label}
                </span>
              </div>
            );
          }
          
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link ${isActive ? 'active' : ''}`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
};

const ProHeader = ({ title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-slate-800">{title}</h1>
        
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-3 py-2">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <span className="font-semibold text-emerald-700">
                {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
              </span>
            </div>
            <div className="text-left">
              <p className="font-medium text-slate-800">{user?.first_name} {user?.last_name}</p>
              <p className="text-sm text-slate-500 capitalize">{user?.role}</p>
            </div>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export const ProLayout = ({ children, title, sidebarItems }) => {
  return (
    <div className="flex min-h-screen bg-slate-50">
      <ProSidebar items={sidebarItems} />
      <div className="flex-1">
        <ProHeader title={title} />
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default ProLayout;
